/**
 * ExtractionReviewPanel — panneau de review parent-agnostique (9.T.5.v3.3).
 *
 * Consommé par :
 *   - `pages/submissions/SubmissionDetailPage` (parentKind='submission')
 *   - `pages/intake-events/IntakeEventDetailPage` (parentKind='intake_event',
 *     conditionnel `status='resolved'` — décision PO Q6)
 *
 * Responsabilité STRICTE : afficher la dernière extraction terminée (status=done)
 * d'un parent + monter `ExtractedFieldsList` en override-mode. Ce composant NE
 * lance PAS de job d'extraction (le lifecycle "lancer + poll" reste dans le
 * legacy `ExtractionPanel` submission-only OU dans les actions v2.2 / v3.2
 * server-side pour intake_event). Séparation propre lecture/écriture.
 *
 * États UX couverts :
 *   - loading   : liste en cours de fetch
 *   - empty     : aucune extraction pour ce parent
 *   - no-done   : extractions présentes mais aucune terminée (pending/running/failed)
 *   - ready     : ≥1 extraction done → monte ExtractedFieldsList
 *
 * Doctrine §2.5 focus-visible + §2.7 i18n neutre `extractions.*`.
 */

import type { ExtractionParentKind, ExtractionRow } from "@kollectivia/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useExtractionsByParent } from "../../lib/queries/extractions";

import { ExtractedFieldsList } from "./ExtractedFieldsList";

export type ExtractionReviewPanelProps = {
  parentKind: ExtractionParentKind;
  parentId: string;
  /**
   * Optional — force l'affichage d'une extraction précise (utile quand le
   * caller connaît la dernière extraction depuis un détail enrichi et veut
   * éviter un flicker de "no-done" pendant le fetch de la liste).
   */
  pinnedExtractionId?: string | null;
};

export function ExtractionReviewPanel({
  parentKind,
  parentId,
  pinnedExtractionId,
}: ExtractionReviewPanelProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const q = useExtractionsByParent(parentKind, parentId);

  const items: ExtractionRow[] = q.data?.items ?? [];
  const activeExtraction = useMemo(() => {
    if (pinnedExtractionId) {
      return items.find((e) => e.id === pinnedExtractionId) ?? null;
    }
    // Dernière `done` (items déjà triés desc created_at côté backend).
    return items.find((e) => e.status === "done") ?? null;
  }, [items, pinnedExtractionId]);

  if (q.isLoading) {
    return (
      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("extractions.review.title")}
        </h2>
        <p className="mt-3 text-sm text-slate-400" aria-live="polite">
          {t("app.loading")}
        </p>
      </section>
    );
  }

  if (q.error) {
    return (
      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("extractions.review.title")}
        </h2>
        <p className="mt-3 text-sm text-red-700" role="alert">
          {q.error instanceof ApiException
            ? getApiErrorMessage(q.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </p>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("extractions.review.title")}
        </h2>
        <p className="mt-3 text-sm text-slate-500">{t("extractions.review.empty")}</p>
      </section>
    );
  }

  if (!activeExtraction) {
    // Extractions présentes mais aucune done (pending/running/failed).
    return (
      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("extractions.review.title")}
        </h2>
        <p className="mt-3 text-sm text-slate-500">{t("extractions.review.no_done_yet")}</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {t("extractions.review.title")}
        </h2>
        {activeExtraction.pass_id && (
          <p className="text-xs text-slate-500" translate="no">
            {activeExtraction.pass_id}
            {activeExtraction.model_id ? ` · ${activeExtraction.model_id}` : ""}
          </p>
        )}
      </header>
      <ExtractedFieldsList
        extractionId={activeExtraction.id}
        parentKind={parentKind}
        parentId={parentId}
      />
    </section>
  );
}
